import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type CookieConsentState = 'accepted' | 'rejected' | 'pending';

const STORAGE_KEY = 'cookie_consent_v1';
const VALID_STATES: CookieConsentState[] = ['accepted', 'rejected'];

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private stateSubject = new BehaviorSubject<CookieConsentState>('pending');

  state$: Observable<CookieConsentState> = this.stateSubject.asObservable();

  constructor() {
    this.hydrate();
  }

  get current(): CookieConsentState {
    return this.stateSubject.value;
  }

  get needsDecision(): boolean {
    return this.stateSubject.value === 'pending';
  }

  get allowsTracking(): boolean {
    return this.stateSubject.value === 'accepted';
  }

  accept(): void {
    this.persist('accepted');
  }

  reject(): void {
    this.persist('rejected');
  }

  reset(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    this.stateSubject.next('pending');
  }

  private hydrate(): void {
    if (typeof window === 'undefined') return;
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as CookieConsentState | null;
      if (stored && VALID_STATES.includes(stored)) {
        this.stateSubject.next(stored);
        this.syncGoogleConsent(stored);
      }
    } catch {}
  }

  private persist(value: CookieConsentState): void {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {}
    this.stateSubject.next(value);
    this.syncGoogleConsent(value);
  }

  private syncGoogleConsent(state: CookieConsentState): void {
    const gtag = (window as any)?.gtag;
    if (typeof gtag !== 'function') return;
    gtag('consent', 'update', {
      analytics_storage: state === 'accepted' ? 'granted' : 'denied',
    });
  }
}
